import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Button, IconButton, Stack} from '@mui/material';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';

import {searchClientsServicesEffect} from '@store/clients/services/clients-services.effects';
import {
  ClientsService,
  clientsServicesQuery,
  clientsServicesService,
  ServiceValidationStatusEnum
} from '@store/clients/services';

import ModalComponent from '@components/modal/Modal.component';
import ManageServiceIndex from '@screens/admin/clients/details/contractedServices/manageService/ManageService.index';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {Colors} from '@constants/colors.constants';
import {FieldValidationType, getFieldError} from '@utils/yup.utils';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridCellParams, GridValueGetterParams} from '@mui/x-data-grid-pro';
import {AddBatchProvenance} from '@screens/admin/operations/executions/addExecutions/AddBatchExecutions.index';
import {ClientsContract} from '@store/clients/contracts';
import {AddQuantity, Remove} from '@icons/index';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import {CheckOutlined, WarningOutlined} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';

interface SelectClientServicesModalProps {
  handleClose: () => void;
  handleBack: () => void;
  handleValidate: (selectedClientServices: { quantity: number; service: ClientsService }[], date?: Date) => void;
  from?: AddBatchProvenance;
  clientsContracts?: ClientsContract[];
}

const SelectClientServicesModal = (props: SelectClientServicesModalProps) => {
  const {handleClose, handleValidate, clientsContracts, handleBack} = props;

  const {t} = useTranslation();

  const [showAddClientService, setShowAddClientService] = useState(false);

  const searchClientsServices = useEffectFn(searchClientsServicesEffect);

  const [{clientsServices, loading}] = useObservable(clientsServicesQuery.clientsServices$);
  const [selectedClientsServices, setSelectedClientsServices] = useState<{
    quantity: number;
    service: ClientsService;
  }[]>([]);

  const [date, setDate] = useState<Date | undefined>(undefined);

  const [sortModel] = useObservable(clientsServicesQuery.sort$);
  const [tablePagination] = useObservable(clientsServicesQuery.pagination$);
  const [filters] = useObservable(clientsServicesQuery.filters$);

  useEffect(() => {
    clientsServicesService.resetFilters();
  }, []);

  useEffect(() => {
    searchClientsServices({
      // filters,
      tablePagination,
      sort: sortModel,
      contractIds: clientsContracts?.map((cc) => cc.id) ?? [],
    });
  }, [clientsContracts, searchClientsServices, sortModel, tablePagination, filters.filterExpiredServices]);

  if (showAddClientService) {
    return (
      <ManageServiceIndex
        handleClose={() => setShowAddClientService(false)}
        handleCreated={(newClientService) => {
          if (newClientService)
            updateQuantityForSelected(newClientService, 1);
        }}
        isCreation/>
    );
  }

  const updateQuantityForSelected = (service: ClientsService, quantity: number) => {
    const selectedServices = [...selectedClientsServices];
    const selectedServiceIndex = selectedServices.findIndex((cs) => cs.service.id === service.id);
    if (selectedServiceIndex > -1) {
      if (quantity <= 0) {
        setSelectedClientsServices(selectedServices.filter((cs) => cs.service.id !== service.id));
      } else {
        selectedServices[selectedServiceIndex].quantity = quantity;
        setSelectedClientsServices([...selectedServices]);
      }
    } else {
      setSelectedClientsServices([...selectedClientsServices, {quantity, service: service}]);
    }
  };

  console.log(filters.filterExpiredServices);

  return (
    <ModalComponent
      aria-label="small-padding"
      handleClose={handleClose}
      maxWidth={false}
      title={t('operations.batchExecutions.selectClientServices')}
      content={(
        <Stack pt="20px" height="85vh" width="100%" spacing={2} overflow="hidden">
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleBack}>
                {t('operations.batchExecutions.back')}
              </Button>
              <Button disabled variant="outlined" color="primary" onClick={() => setShowAddClientService(true)}>
                {t('operations.batchExecutions.addButton')}
              </Button>
            </Stack>
            <CheckboxComponent
              label={t('clients.details.contractedServices.manage.selectContract.table.filterExpiredServices')}
              value={filters.filterExpiredServices}
              handleChange={clientsServicesService.setFilters('filterExpiredServices')}
            />
          </Stack>
          <NewTableComponent
            loading={loading}
            columns={[
              {
                minWidth: 200,
                renderCell: (params: GridCellParams) => {
                  const selectedService = selectedClientsServices.find((cs) => cs.service.id === params.row.id);
                  const quantity = selectedService?.quantity || 0;
                  return (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton
                        aria-label="quantity"
                        centerRipple={false}
                        onClick={() => {
                          updateQuantityForSelected(params.row, quantity - 1);
                        }}
                      >
                        <Remove/>
                      </IconButton>
                      <NumberTextFieldComponent
                        value={quantity}
                        handleChange={(quantity) => updateQuantityForSelected(params.row, quantity || 0)}
                      />
                      <IconButton
                        aria-label="quantity"
                        centerRipple={false}
                        onClick={() => {
                          updateQuantityForSelected(params.row, quantity + 1);
                        }}
                      >
                        <AddQuantity/>
                      </IconButton>
                    </Stack>
                  );
                },
                field: 'quantity'
              },
              COLUMN_TYPES.ICON({
                field: 'execution.isCancelled',
                color: 'secondary',
                label: '',
                minWidth: 30,
                width: 30,
                renderIcon: (service: ClientsService) => service.validationStatus === ServiceValidationStatusEnum.WAITING_FOR_SP ?
                  <Tooltip title="En attente de prix de vente">
                    <WarningOutlined color="warning" fontSize="small"/>
                  </Tooltip> : <CheckOutlined color="success"></CheckOutlined>,
              }),
              COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'clientOrganisationUnitName',
                label: t('clients.servicesTable.clientUnitName')
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'service.clientWasteName',
                label: t('clients.servicesTable.name'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'schedulesCount',
                label: t('clients.servicesTable.frequency'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'kind.kind',
                label: t('clients.servicesTable.type'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'waste.name',
                label: t('clients.servicesTable.details'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'container.name',
                label: t('clients.servicesTable.container'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sOrg.name',
                label: t('clients.servicesTable.supplier'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<ClientsService, ClientsService>) => params.row.supplierOrganisationUnitName,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'buyPriceUnit',
                label: t('clients.servicesTable.purchasePrice'),
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.buyPriceIfOnlyOne !== null
                  ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
                  : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'sellPriceUnit',
                label: t('clients.servicesTable.sellingPrice'),
                cellClassName: (params: GridCellParams<ClientsService>) => params.row.buyPriceIfOnlyOne && params.row.sellPriceIfOnlyOne && params.row.buyPriceIfOnlyOne > params.row.sellPriceIfOnlyOne ? 'error' : '',
                valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.sellPriceIfOnlyOne !== null
                  ? `${params.row.sellPriceIfOnlyOne / 100} ${params.row.sellPriceCurrency || ''}`
                  : `${params.row.sellPricesCount || '-'}${params.row.sellPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                width: 130,
              }),
            ]}
            handlePageSizeChange={clientsServicesService.setPagination('limit')}
            pageSize={tablePagination.limit}
            handlePageChange={(page) => clientsServicesService.setPagination('page')(page + 1)}
            page={tablePagination.page - 1}
            handleSortModelChange={clientsServicesService.setSort}
            sortModel={[sortModel]}
            disableRowSelection={true}
            totalRows={clientsServicesQuery.totalRows}
            data={clientsServices}/>
        </Stack>
      )}
      actions={(
        <Stack direction="row" alignItems="center" position="relative">
          <Box
            position="absolute"
            left={-100}
            sx={{transform: 'translateX(-100%)', '& *::placeholder': {color: Colors.textWithOpacity}}}>
            <DatePickerComponent
              inputWidth={200}
              size="small"
              handleChange={setDate}
              value={date}
              placeholder={t('operations.batchExecutions.date')}/>
          </Box>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedClientsServices.length || !!getFieldError(date, FieldValidationType.DATE)}
            onClick={() => handleValidate(selectedClientsServices, date)}>
            {t('global.validate')}
          </Button>
        </Stack>
      )}/>
  );
};

export default SelectClientServicesModal;
